import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { fileSchemaOptional } from '@/utils/libs/schemaUtil';

interface MediaFile {
  url: string;
  name: string;
  type: string;
}

export const resultSchema = z.object({
  id: z.string().optional(),
  test_run_detail_id: z.string().optional(),
  status: z.string({ required_error: 'Status is required' }).min(1, 'Status is required'),
  comment_test: z
    .string({ required_error: 'Comment is required' })
    .min(1, 'Comment is required')
    .refine(
      val => {
        // Remove HTML tags, &nbsp;, and whitespace to ensure content is meaningful
        const textWithoutTags = val
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/gi, '')
          .trim();
        return textWithoutTags.length > 0;
      },
      { message: 'Comment is required' },
    ),
  medias: fileSchemaOptional,
});

export type IResultSchema = z.infer<typeof resultSchema>;

export const ResultService = {
  useGet: (id?: string) =>
    useQuery<IResultSchema[]>({
      queryKey: ['result', id],
      queryFn: async () => {
        if (!id) return [];

        const response = await axiosInstance.get(
          `/api/crud/test-case/${id}/result`,
        );
        return response.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IResultSchema) => {
        const response = await axiosInstance.post('api/crud/result', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
      },
    }),

  useExport: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.get(
          `/api/crud/test-run/${id}/export`,
          {
            responseType: 'blob',
          },
        );

        return response.data;
      },
    }),

  useGetMedia: () =>
    useQuery<MediaFile[]>({
      queryKey: ['resultMedia'],
      queryFn: async () => {
        const response = await axiosInstance.get(`/api/crud/result`);

        return response.data.flatMap(
          (result: IResultSchema) => result.medias || [],
        );
      },
    }),
};
